// lib/gclid.js
// Captura y persistencia de los click ids de Google Ads (gclid, gbraid, wbraid).
//
// Al aterrizar desde un ad de Google la URL trae uno de ?gclid= / ?gbraid= / ?wbraid=.
// gclid es el de siempre (desktop/Android); gbraid (app->web) y wbraid (web->web) son
// los que Google entrega en iOS/Safari con tracking restringido, JUSTO el tráfico donde
// el gtag del navegador más falla. Los persistimos en cookies first-party (90 días =
// ventana de conversión por click) y en localStorage (Safari/ITP capa las cookies JS a
// 7 días). En el checkout se leen con getGclid()/getGbraid()/getWbraid() y viajan en
// ad.* hasta sales.gclid/gbraid/wbraid (patrón fbp/fbc -> CAPI), para el respaldo
// server-side de conversiones (upload offline por click id).

import { getCookie } from "@/utils/cookies";

const KEYS = { gclid: "bcm_gclid", gbraid: "bcm_gbraid", wbraid: "bcm_wbraid" };
const MAX_AGE_DAYS = 90;

function persistId(name, value) {
  const key = KEYS[name];
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${key}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
  try {
    window.localStorage.setItem(key, JSON.stringify({ v: value, t: Date.now() }));
  } catch {
    /* localStorage puede estar bloqueado (modo privado) */
  }
}

/**
 * Lee ?gclid=/?gbraid=/?wbraid= de la URL actual y los persiste.
 * Llamar una vez por pageload (GoogleAdsTag).
 */
export function captureClickIds() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const name of Object.keys(KEYS)) {
      const v = params.get(name);
      if (v && v.length <= 200) persistId(name, v);
    }
  } catch {
    /* nunca romper la página por analytics */
  }
}

/** Lee un click id persistido: cookie propia -> localStorage (si no venció). */
function readStored(name) {
  if (typeof window === "undefined") return null;

  const own = getCookie(KEYS[name]);
  if (own) return own;

  try {
    const raw = window.localStorage.getItem(KEYS[name]);
    if (raw) {
      const { v, t } = JSON.parse(raw);
      if (v && t && Date.now() - t < MAX_AGE_DAYS * 864e5) return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Devuelve el gclid vigente o null.
 * Orden: cookie propia -> localStorage (si no venció) -> _gcl_aw de gtag (GCL.<ts>.<gclid>).
 */
export function getGclid() {
  const own = readStored("gclid");
  if (own) return own;

  const gclAw = getCookie("_gcl_aw");
  if (gclAw) {
    const parts = gclAw.split(".");
    if (parts.length >= 3 && parts[2]) return parts.slice(2).join(".");
  }

  return null;
}

/** gbraid vigente o null (iOS app->web). Sin fallback a _gcl_aw (ese es solo gclid). */
export function getGbraid() {
  return readStored("gbraid");
}

/** wbraid vigente o null (iOS web->web). */
export function getWbraid() {
  return readStored("wbraid");
}
